import React from "react";
import IdleTimer from "react-idle-timer";
import { withRouter } from "react-router-dom";
import withStyles from "@material-ui/core/styles/withStyles";
import { withGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  getDataNoCache,
  getToken,
  postData,
  removeToken,
  setToken,
  setAccountId
} from "../../state/api";
import makeCancelable from "../../util/makeCancelable";
import { Provider, defaultState } from "./context";

const HideRecaptchaIcon = withStyles({
  "@global": {
    ".grecaptcha-badge": {
      opacity: 0
    }
  }
})(({ children }) => children);

const ShowRecaptchaIcon = withStyles({
  "@global": {
    ".grecaptcha-badge": {
      bottom: "75px !important"
    }
  }
})(({ children }) => children);

class AuthProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...defaultState,
      refreshAuth: this.refreshAuth,
      logout: this.logout,
      register: this.register,
      resetPassword: this.resetPassword,
      changePassword: this.changePassword
    };
  }

  componentDidMount() {
    if (this.props.alwaysAuth) removeToken();
    const token = getToken();
    if (!token) {
      this.setState({ isVerifying: false });
      return;
    }

    this.state.refreshAuth({
      token,
      remember: Boolean(localStorage.getItem("token"))
    });
  }

  componentWillUnmount = () => this.cancel && this.cancel();

  toggleLoading = () => {
    this.setState({
      isLoading: true,
      userInfo: null,
      error: null
    });
  };

  logout = () => {
    removeToken();
    this.setStatus(null, null);
  };

  setStatus = (userInfo, error, cb) => {
    if (!userInfo) removeToken();

    this.setState(
      {
        userInfo,
        error,
        isLoading: false,
        isVerifying: false
      },
      cb
    );
  };

  executeReCaptcha = method => {
    if (this.props.disableReCaptcha) {
      return Promise.resolve("NONE");
    }

    return this.props.googleReCaptchaProps.executeRecaptcha(method);
  };

  refreshAuth = (auth, cb) => {
    this.toggleLoading();

    if (auth.token) setToken(auth.token, auth.remember);
    else removeToken();

    this.cancel = makeCancelable(
      this.executeReCaptcha("refreshAuth").then(recaptcha => {
        if (auth.token !== undefined) {
          return getDataNoCache(this.props.ping, {
            headers: { "x-recaptcha": recaptcha }
          });
        } else if (auth.username) {
          return postData(
            this.props.auth,
            {
              username: auth.username,
              password: auth.password
            },
            { headers: { "x-recaptcha": recaptcha } }
          );
        } else {
          return Promise.resolve({
            success: true,
            signature: auth.signature
          });
        }
      }),
      result => {
        if (result.success) {
          const tok =
            auth.token || result.message
              ? auth.token || "Bearer " + result.message
              : auth.signature;
          setToken(tok, auth.remember);
          if (auth.account_id) setAccountId(auth.account_id);

          this.setStatus(tok, null, cb);
        } else {
          this.setStatus(null, result.message, cb);
        }
      },
      () => {
        this.setStatus(null, "Network Error", cb);
      }
    );
  };

  resetPassword = email => {
    return new Promise((pass, fail) => {
      setTimeout(() => {
        this.cancel = makeCancelable(
          this.executeReCaptcha("resetPassword").then(recaptcha =>
            postData(
              this.props.passwordReset,
              {
                email,
                callback: this.props.passwordResetCallback
              },
              { headers: { "x-recaptcha": recaptcha } }
            )
          ),
          result => {
            if (!result.success) {
              fail(result);
              return;
            }

            pass(result);
          },
          e => {
            fail({
              result: {
                success: false,
                error: e
              }
            });
          }
        );
      }, 200);
    });
  };

  changePassword = (new_password, signature) => {
    return new Promise((pass, fail) => {
      setTimeout(() => {
        this.cancel = makeCancelable(
          this.executeReCaptcha("changePassword").then(recaptcha =>
            postData(
              this.props.passwordResetToken,
              {
                signature,
                new_password
              },
              { headers: { "x-recaptcha": recaptcha } }
            )
          ),
          pass,
          fail
        );
      }, 200);
    });
  };

  register = (body, cb) => {
    this.toggleLoading();

    setTimeout(() => {
      removeToken();
      this.cancel = makeCancelable(
        this.executeReCaptcha("register").then(recaptcha =>
          postData(this.props.register, body, {
            headers: { "x-recaptcha": recaptcha }
          })
        ),
        result => {
          this.setStatus(null, null);
          cb(result);
        },
        () => {
          this.setStatus(null, "Network Error");
        }
      );
    }, 200);
  };

  onIdle = () =>
    this.state.userInfo &&
    window.location.pathname !== this.props.logoutUrl &&
    this.props.history.push(this.props.logoutUrl);

  render = () => (
    <>
      {!this.props.alwaysAuth && (
        <IdleTimer
          ref={ref => (this.idleTimer = ref)}
          element={document}
          onIdle={this.onIdle}
          debounce={250}
          timeout={this.props.idleTimeoutMs}
        />
      )}
      <Provider value={this.state}>
        {this.state.userInfo ? (
          <HideRecaptchaIcon>{this.props.children}</HideRecaptchaIcon>
        ) : (
          <ShowRecaptchaIcon>{this.props.children}</ShowRecaptchaIcon>
        )}
      </Provider>
    </>
  );
}

export default withRouter(
  withStyles({}, { name: "LcAuthProvider" })(withGoogleReCaptcha(AuthProvider))
);
